// ═══════════════════════════════════════════════════════════════════════════════
// MXDR365 — Shared UI Components
// ═══════════════════════════════════════════════════════════════════════════════
import { CSSProperties, ReactNode, MouseEvent, useState } from 'react';
import { theme, fonts } from '../../lib/theme';
import { useDisplayName } from '../../hooks/useDisplayName';
import { looksLikeUuid } from '../../lib/displayName';

// ─── Icons ───
// Stroke-based SVG icon set. 20×20 for primary slots, 16×16 for secondary.
const _i: CSSProperties & { viewBox: string; fill: string; stroke: string; strokeWidth: number; strokeLinecap: 'round'; strokeLinejoin: 'round' } = {
  display: 'block',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as any; // spread onto <svg> elements

const ico20 = { width: 20, height: 20, ..._i } as any;
const ico16 = { width: 16, height: 16, ..._i } as any;
const ico18 = { width: 18, height: 18, ..._i } as any;

export const Icons = {
  // ── 20px — stat cards and primary content ──
  Document:      () => <svg {...ico20}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  Building:      () => <svg {...ico20}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 21V9h6v12"/><path d="M3 9h18"/></svg>,
  CheckSquare:   () => <svg {...ico20}><path d="M9 11l3 3 8-8"/><path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9"/></svg>,
  GraduationCap: () => <svg {...ico20}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5"/></svg>,
  Shield:        () => <svg {...ico20}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  BarChart:      () => <svg {...ico20}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 17V13"/><path d="M12 17V9"/><path d="M17 17V5"/></svg>,
  Users:         () => <svg {...ico20}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Bell:          () => <svg {...ico20}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  // ── 18px — toolbar and utility ──
  Search:        () => <svg {...ico18}><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>,
  Settings:      () => <svg {...ico18}><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>,
  // ── 16px — section headers and inline ──
  Grid:          () => <svg {...ico16}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  Activity:      () => <svg {...ico16}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  BookOpen:      () => <svg {...ico16}><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
  Briefcase:     () => <svg {...ico16}><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  Clock:         () => <svg {...ico16}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
  Tag:           () => <svg {...ico16}><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
  AlertTriangle: () => <svg {...ico16}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  ChevronRight:  () => <svg {...ico16}><polyline points="9 18 15 12 9 6"/></svg>,
  // Share icon — three connected dots (share/export glyph)
  Share:         () => <svg {...ico16}><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
  Link:          () => <svg {...ico16}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
};

// ─── Badge ───
interface BadgeProps {
  children: ReactNode;
  color?: string;
  bg?: string;
  sm?: boolean;
  onClick?: () => void;
  style?: CSSProperties;
}

export function Badge({ children, color = theme.accent, bg, sm, onClick, style }: BadgeProps) {
  return (
    <span
      onClick={onClick}
      style={{
        ...style,
        fontSize: sm ? 9 : 10,
        fontWeight: 600,
        padding: sm ? '1px 5px' : '2px 7px',
        borderRadius: 4,
        background: bg || `${color}1a`,
        color,
        letterSpacing: 0.3,
        whiteSpace: 'nowrap',
        display: 'inline-block',
        cursor: onClick ? 'pointer' : 'default',
        border: `1px solid ${color}30`,
      }}
    >
      {children}
    </span>
  );
}

// ─── ShareButton ───
// Copies a direct link to the current item (article / folder / KB) to the
// clipboard and flashes a confirmation. The URL is built by overlaying the
// given hash params onto the current origin+path.
interface ShareButtonProps {
  /** Hash params to put in the shared URL (e.g. { v: 'article', id: 'abc', kb: 'my-kb' }) */
  params: Record<string, string>;
  /** Optional label — defaults to icon-only. */
  label?: string;
  /** When true, renders as a small icon-only button instead of a regular button. */
  iconOnly?: boolean;
  /** Stop event propagation on click (useful when embedded inside a clickable card). */
  stopPropagation?: boolean;
  /** Optional inline style override */
  style?: CSSProperties;
  /** Optional title attribute override */
  title?: string;
}

export function ShareButton({ params, label, iconOnly, stopPropagation, style, title }: ShareButtonProps) {
  const [copied, setCopied] = useState(false);

  async function handleClick(e: MouseEvent<HTMLButtonElement>) {
    if (stopPropagation) e.stopPropagation();
    const qs = new URLSearchParams(params).toString();
    const url = `${window.location.origin}${window.location.pathname}#${qs}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback for older browsers / blocked clipboard
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
      document.body.removeChild(ta);
    }
  }

  const baseStyle: CSSProperties = iconOnly
    ? {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: copied ? theme.green : theme.textTertiary,
        padding: 4,
        borderRadius: 4,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'color .15s, background .15s',
      }
    : {
        background: copied ? `${theme.green}15` : 'transparent',
        border: `1px solid ${copied ? theme.green : theme.border}`,
        color: copied ? theme.green : theme.text,
        padding: '4px 10px',
        fontSize: 11,
        fontWeight: 500,
        borderRadius: 6,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontFamily: 'inherit',
        transition: 'all .15s',
      };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={title ?? (copied ? 'Link copied!' : 'Copy share link')}
      style={{ ...baseStyle, ...style }}
    >
      {copied ? <Icons.CheckSquare /> : <Icons.Share />}
      {!iconOnly && <span>{copied ? 'Copied!' : (label ?? 'Share')}</span>}
    </button>
  );
}

// ─── Avatar ───
// ─── UserName — declarative display-name resolution ─────────────────────────
// Resolve an OID/UPN to a real display name backed by the registeredUsers
// directory.  Falls back to `storedName` (e.g. a denormalised authorName on
// the record), then the UPN local-part, then the raw input.  Identity keys
// (the OID/UPN itself) are NEVER changed — only the rendered string is.

interface UserNameProps {
  /** Entra OID or UPN to resolve.  May be a denormalised author email. */
  upn: string | undefined | null;
  /** Display name already stored on the record (e.g. post.authorName).
   *  Used only when it isn't itself a UPN. */
  storedName?: string;
  /** Inline style for the wrapper span. */
  style?: CSSProperties;
  /** Optional className for the wrapper span. */
  className?: string;
  /** Rendered when resolution fails — i.e. the directory has no match and the
   *  resolver degraded to an empty string or a raw UUID/OID.  Defaults to
   *  'Unknown'.  A raw GUID is NEVER rendered, regardless of this value. */
  fallback?: string;
}

export function UserName({ upn, storedName, style, className, fallback = 'Unknown' }: UserNameProps) {
  const name = useDisplayName(upn, storedName);
  // Never leak a raw object id: if resolution degraded all the way back to a
  // bare UUID (user not in the directory, e.g. left the org), show the fallback.
  const display = !name || looksLikeUuid(name) ? fallback : name;
  return (
    <span style={style} className={className}>
      {display}
    </span>
  );
}

interface AvatarProps {
  name: string;
  size?: number;
  id?: string;
  /** Show a green online dot at the bottom-right when true. */
  online?: boolean;
}

export function Avatar({ name, size = 28, id, online }: AvatarProps) {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const hue = id ? parseInt(id.replace(/\D/g, '') || '0') * 47 : 200;
  const dotSize = Math.max(6, Math.round(size * 0.28));

  const avatar = (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `hsl(${hue}, 55%, 32%)`,
        border: `1px solid hsl(${hue}, 55%, 45%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.36,
        fontWeight: 700,
        color: '#fff',
        flexShrink: 0,
        letterSpacing: 0.5,
      }}
    >
      {initials}
    </div>
  );

  if (!online) return avatar;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      {avatar}
      <span
        title="Online"
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: dotSize,
          height: dotSize,
          borderRadius: '50%',
          background: '#22c55e',
          border: '1.5px solid #0a0f1a',
          boxShadow: '0 0 0 1px rgba(34,197,94,0.35)',
        }}
      />
    </div>
  );
}

// ─── AssigneeStack — overlapping avatar pile with "+N" overflow ──────────────
// Renders multi-assignee chips as a stacked avatar pile; degrades gracefully
// when only a single id is present so existing single-assignee surfaces look
// identical until they migrate to passing an array.
interface AssigneeStackProps {
  ids: string[];
  size?: number;
  max?: number;
  /** When true, the first hover tooltip is rendered as a "+more" summary
   *  with every assignee's name on separate lines (used on dense cards). */
  showOverflow?: boolean;
}

export function AssigneeStack({ ids, size = 22, max = 3, showOverflow = true }: AssigneeStackProps) {
  const cleanIds = (ids || []).filter(Boolean);
  if (cleanIds.length === 0) {
    return (
      <span style={{ fontSize: 10, color: theme.textTertiary }}>Unassigned</span>
    );
  }
  const visible = cleanIds.slice(0, max);
  const extra = cleanIds.length - visible.length;
  const overlap = Math.round(size * 0.4);
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center' }}
      title={showOverflow ? cleanIds.join('\n') : undefined}
    >
      {visible.map((id, idx) => (
        <span
          key={id + idx}
          style={{
            marginLeft: idx === 0 ? 0 : -overlap,
            border: `2px solid ${theme.card}`,
            borderRadius: '50%',
            zIndex: visible.length - idx,
            display: 'inline-flex',
          }}
        >
          <AssigneeChipAvatar id={id} size={size} />
        </span>
      ))}
      {extra > 0 && (
        <span
          style={{
            marginLeft: -overlap,
            background: theme.input,
            color: theme.textSecondary,
            border: `2px solid ${theme.card}`,
            borderRadius: '50%',
            width: size, height: size,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: Math.max(9, size * 0.35), fontWeight: 700,
            zIndex: 0,
          }}
        >
          +{extra}
        </span>
      )}
    </span>
  );
}

function AssigneeChipAvatar({ id, size }: { id: string; size: number }) {
  // Resolve name through the directory so the avatar's initials match the
  // display name shown elsewhere (no UPN initials).
  const name = useDisplayName(id) || id || '?';
  return <Avatar name={name} size={size} id={id} />;
}

// ─── AssigneePicker — multi-select user picker (Planner-style chips) ─────────
interface PickerUser {
  id: string;
  name: string;
  email?: string;
}

interface AssigneePickerProps {
  users: PickerUser[];
  selectedIds: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** Display label above the chips. */
  label?: string;
}

export function AssigneePicker({ users, selectedIds, onChange, disabled, label }: AssigneePickerProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const selected = selectedIds.filter(Boolean);
  const selectedSet = new Set(selected);
  const remaining = users.filter(u =>
    !selectedSet.has(u.id) &&
    (!filter.trim() ||
      u.name.toLowerCase().includes(filter.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(filter.toLowerCase())),
  );

  function add(id: string) {
    if (disabled) return;
    if (selectedSet.has(id)) return;
    onChange([...selected, id]);
  }
  function remove(id: string) {
    if (disabled) return;
    onChange(selected.filter(s => s !== id));
  }

  return (
    <div style={{ position: 'relative' }}>
      {label && (
        <div style={{ fontSize: 10, color: theme.textTertiary, marginBottom: 4, fontWeight: 600 }}>{label}</div>
      )}
      <div
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center',
          padding: '6px 8px', borderRadius: 6,
          border: `1px solid ${theme.border}`, background: theme.input,
          cursor: disabled ? 'not-allowed' : 'text',
          minHeight: 32,
        }}
        onClick={() => !disabled && setOpen(true)}
      >
        {selected.map(id => {
          const u = users.find(x => x.id === id);
          return (
            <span
              key={id}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: `${theme.accent}22`, color: theme.text,
                borderRadius: 12, padding: '2px 6px 2px 2px',
                border: `1px solid ${theme.accent}55`, fontSize: 11,
              }}
            >
              <AssigneeChipAvatar id={id} size={18} />
              <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {u?.name || id}
              </span>
              {!disabled && (
                <button
                  onClick={e => { e.stopPropagation(); remove(id); }}
                  style={{ background: 'none', border: 'none', color: theme.textTertiary, cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1 }}
                  aria-label="Remove assignee"
                >×</button>
              )}
            </span>
          );
        })}
        {!disabled && (
          <input
            value={filter}
            onChange={e => { setFilter(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder={selected.length === 0 ? 'Add assignees…' : ''}
            style={{
              flex: '1 0 80px', minWidth: 80, padding: '2px 4px',
              border: 'none', outline: 'none', background: 'transparent',
              color: theme.text, fontSize: 12, fontFamily: 'inherit',
            }}
          />
        )}
      </div>
      {open && !disabled && remaining.length > 0 && (
        <div
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
            marginTop: 4, background: theme.card, border: `1px solid ${theme.border}`,
            borderRadius: 6, maxHeight: 220, overflowY: 'auto',
            boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
          }}
        >
          {remaining.slice(0, 20).map(u => (
            <button
              key={u.id}
              onMouseDown={e => { e.preventDefault(); add(u.id); setFilter(''); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '6px 10px', textAlign: 'left',
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: theme.text, fontSize: 12, fontFamily: 'inherit',
              }}
            >
              <Avatar name={u.name} size={20} id={u.id} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
              {u.email && <span style={{ fontSize: 10, color: theme.textTertiary }}>{u.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── RecurrencePicker — Outlook-style recurrence editor ─────────────────────
// Edits a TaskRecurrence struct. Empty / null = non-recurring. Used by the
// Tasks Add modal and the Projects task detail panel.
import type { TaskRecurrence, RecurrenceFrequency } from '../../types';

interface RecurrencePickerProps {
  value: TaskRecurrence | null | undefined;
  onChange: (next: TaskRecurrence | null) => void;
  disabled?: boolean;
}

const DOW_OPTIONS: Array<{ id: number; label: string }> = [
  { id: 0, label: 'S' },
  { id: 1, label: 'M' },
  { id: 2, label: 'T' },
  { id: 3, label: 'W' },
  { id: 4, label: 'T' },
  { id: 5, label: 'F' },
  { id: 6, label: 'S' },
];

const FREQUENCY_OPTIONS: Array<{ id: RecurrenceFrequency; label: string }> = [
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'biweekly', label: 'Every 2 weeks' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
];

type EndKind = 'never' | 'date' | 'count';

export function RecurrencePicker({ value, onChange, disabled }: RecurrencePickerProps) {
  const enabled = !!value;
  const rec: TaskRecurrence = value || {
    frequency: 'weekly', interval: 1, daysOfWeek: [], endsOn: null,
  };
  const endKind: EndKind =
    rec.count && rec.count > 0 ? 'count' : rec.endsOn ? 'date' : 'never';

  function update(patch: Partial<TaskRecurrence>) {
    onChange({ ...rec, ...patch });
  }

  function toggleDow(d: number) {
    const current = rec.daysOfWeek || [];
    const next = current.includes(d) ? current.filter(x => x !== d) : [...current, d];
    update({ daysOfWeek: next });
  }

  return (
    <div style={{ border: `1px solid ${theme.border}`, borderRadius: 6, padding: 10, background: theme.input }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: enabled ? 10 : 0, fontSize: 12, fontWeight: 600 }}>
        <input
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={e => onChange(e.target.checked ? rec : null)}
        />
        Repeats
      </label>
      {enabled && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: 6 }}>
            <select
              value={rec.frequency}
              disabled={disabled}
              onChange={e => update({ frequency: e.target.value as RecurrenceFrequency })}
              style={pickerInputStyle}
            >
              {FREQUENCY_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <input
              type="number"
              min={1}
              max={99}
              value={rec.interval || 1}
              disabled={disabled}
              onChange={e => update({ interval: Math.max(1, parseInt(e.target.value, 10) || 1) })}
              style={pickerInputStyle}
              title="Every N intervals"
            />
          </div>

          {rec.frequency === 'weekly' && (
            <div>
              <div style={{ fontSize: 10, color: theme.textTertiary, marginBottom: 4 }}>
                Days of week (leave empty to use the task's anchor day)
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {DOW_OPTIONS.map(d => {
                  const active = (rec.daysOfWeek || []).includes(d.id);
                  return (
                    <button
                      key={d.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => toggleDow(d.id)}
                      style={{
                        width: 28, height: 28, borderRadius: '50%',
                        border: `1px solid ${active ? theme.accent : theme.border}`,
                        background: active ? theme.accent : theme.card,
                        color: active ? '#fff' : theme.textSecondary,
                        fontSize: 11, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {(rec.frequency === 'monthly' || rec.frequency === 'quarterly') && (
            <div>
              <div style={{ fontSize: 10, color: theme.textTertiary, marginBottom: 4 }}>
                Day of month (1–31; clamps when shorter)
              </div>
              <input
                type="number"
                min={1}
                max={31}
                value={rec.dayOfMonth ?? ''}
                disabled={disabled}
                placeholder="anchor"
                onChange={e => {
                  const n = parseInt(e.target.value, 10);
                  update({ dayOfMonth: isNaN(n) ? undefined : Math.min(31, Math.max(1, n)) });
                }}
                style={{ ...pickerInputStyle, width: 80 }}
              />
            </div>
          )}

          <div>
            <div style={{ fontSize: 10, color: theme.textTertiary, marginBottom: 4 }}>Ends</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={pickerRadioRow}>
                <input
                  type="radio"
                  checked={endKind === 'never'}
                  disabled={disabled}
                  onChange={() => update({ endsOn: null, count: undefined })}
                />
                Never
              </label>
              <label style={pickerRadioRow}>
                <input
                  type="radio"
                  checked={endKind === 'date'}
                  disabled={disabled}
                  onChange={() => update({ endsOn: rec.endsOn || isoToday(), count: undefined })}
                />
                On
                <input
                  type="date"
                  value={(rec.endsOn || '').slice(0, 10)}
                  disabled={disabled || endKind !== 'date'}
                  onChange={e => update({ endsOn: e.target.value || null, count: undefined })}
                  style={{ ...pickerInputStyle, width: 150 }}
                />
              </label>
              <label style={pickerRadioRow}>
                <input
                  type="radio"
                  checked={endKind === 'count'}
                  disabled={disabled}
                  onChange={() => update({ count: rec.count || 10, endsOn: null })}
                />
                After
                <input
                  type="number"
                  min={1}
                  max={999}
                  value={rec.count ?? ''}
                  disabled={disabled || endKind !== 'count'}
                  onChange={e => {
                    const n = parseInt(e.target.value, 10);
                    update({ count: isNaN(n) ? undefined : Math.max(1, n), endsOn: null });
                  }}
                  style={{ ...pickerInputStyle, width: 70 }}
                />
                occurrences
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

const pickerInputStyle: CSSProperties = {
  padding: '5px 8px', borderRadius: 4, border: `1px solid ${theme.border}`,
  background: theme.card, color: theme.text, fontSize: 11, fontFamily: 'inherit',
};

const pickerRadioRow: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: theme.textSecondary, fontFamily: 'inherit',
};

// ─── Button ───
interface ButtonProps {
  children: ReactNode;
  onClick?: (e: MouseEvent) => void;
  primary?: boolean;
  sm?: boolean;
  danger?: boolean;
  disabled?: boolean;
  style?: CSSProperties;
}

export function Button({ children, onClick, primary, sm, danger, disabled, style }: ButtonProps) {
  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: sm ? '4px 10px' : '7px 14px',
    fontSize: sm ? 11 : 12,
    fontWeight: 500,
    borderRadius: 6,
    border: '1px solid transparent',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit',
    transition: 'all .15s',
    opacity: disabled ? 0.45 : 1,
    letterSpacing: 0.1,
    ...style,
  };

  if (primary) {
    Object.assign(base, {
      background: theme.accent,
      color: theme.accentText,
      fontWeight: 600,
      borderColor: theme.accent,
    });
  } else if (danger) {
    Object.assign(base, {
      background: theme.redGlow,
      color: theme.red,
      borderColor: `${theme.red}33`,
    });
  } else {
    Object.assign(base, {
      background: theme.input,
      color: theme.textSecondary,
      borderColor: theme.border,
    });
  }

  return (
    <button style={base} onClick={disabled ? undefined : onClick}>
      {children}
    </button>
  );
}

// ─── StatCard ───
// Professional KPI metric tile used on dashboards
interface StatCardProps {
  label: string;
  value: string | number;
  icon?: ReactNode;
  color?: string;
}

export function StatCard({ label, value, icon, color }: StatCardProps) {
  const c = color || theme.accent;
  return (
    <div
      style={{
        flex: '1 1 160px',
        background: theme.card,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        padding: '16px 18px 14px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Accent top line */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, ${c}, ${c}55)`,
      }} />

      {/* Label + icon row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{
          fontSize: 10, fontWeight: 600, color: theme.textTertiary,
          textTransform: 'uppercase', letterSpacing: 0.8, lineHeight: 1.4,
        }}>
          {label}
        </div>
        {icon && (
          <div style={{ color: c, opacity: 0.75, marginTop: -1 }}>
            {icon}
          </div>
        )}
      </div>

      {/* Metric value */}
      <div style={{
        fontSize: 28, fontWeight: 700, color: theme.text,
        fontFamily: fonts.mono, lineHeight: 1, letterSpacing: -0.5,
      }}>
        {value}
      </div>
    </div>
  );
}

// ─── Progress Bar ───
interface ProgressBarProps {
  percent: number;
  color?: string;
  height?: number;
}

export function ProgressBar({ percent, color = theme.green, height = 4 }: ProgressBarProps) {
  return (
    <div
      style={{
        height,
        background: `${theme.border}88`,
        borderRadius: height,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${Math.min(100, Math.max(0, percent))}%`,
          height: '100%',
          background: color,
          borderRadius: height,
          transition: 'width .4s ease',
        }}
      />
    </div>
  );
}

// ─── Tab Button ───
interface TabButtonProps {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
}

export function TabButton({ children, active, onClick }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '5px 12px',
        fontSize: 11,
        fontWeight: active ? 600 : 400,
        borderRadius: 5,
        border: active ? `1px solid ${theme.accent}33` : '1px solid transparent',
        cursor: 'pointer',
        fontFamily: 'inherit',
        background: active ? `${theme.accent}14` : 'transparent',
        color: active ? theme.accent : theme.textSecondary,
        transition: 'all .15s',
        letterSpacing: 0.1,
      }}
    >
      {children}
    </button>
  );
}

// ─── Card ───
interface CardProps {
  children: ReactNode;
  onClick?: () => void;
  hoverable?: boolean;
  style?: CSSProperties;
  padding?: number | string;
}

export function Card({ children, onClick, hoverable = false, style, padding = 20 }: CardProps) {
  return (
    <div
      onClick={onClick}
      style={{
        background: theme.card,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        padding,
        cursor: onClick || hoverable ? 'pointer' : 'default',
        transition: 'border-color .15s, box-shadow .15s',
        ...style,
      }}
      onMouseEnter={(e) => {
        if (hoverable || onClick) {
          e.currentTarget.style.borderColor = `${theme.accent}40`;
          e.currentTarget.style.boxShadow = `0 0 0 1px ${theme.accent}15`;
        }
      }}
      onMouseLeave={(e) => {
        if (hoverable || onClick) {
          e.currentTarget.style.borderColor = theme.border;
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      {children}
    </div>
  );
}

// ─── Card Section Title ───
// Lightweight heading for sections within a Card (replaces emoji-prefixed divs)
interface CardTitleProps {
  children: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
}

export function CardTitle({ children, icon, action }: CardTitleProps) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        {icon && <span style={{ color: theme.textTertiary }}>{icon}</span>}
        <span style={{ fontWeight: 600, fontSize: 13, color: theme.text }}>{children}</span>
      </div>
      {action}
    </div>
  );
}

// ─── Section Header ───
interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  badge?: ReactNode;
}

export function SectionHeader({ title, subtitle, action, badge }: SectionHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 20,
      }}
    >
      <div>
        <h1 style={{ margin: '0 0 3px', fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, letterSpacing: -0.3 }}>
          {title}
          {badge}
        </h1>
        {subtitle && <p style={{ margin: 0, fontSize: 12, color: theme.textTertiary, letterSpacing: 0.1 }}>{subtitle}</p>}
      </div>
      {action && <div style={{ display: 'flex', gap: 6 }}>{action}</div>}
    </div>
  );
}

// ─── Empty State ───
interface EmptyStateProps {
  icon?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}

export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      {icon && <div style={{ fontSize: 28, marginBottom: 14, opacity: 0.5 }}>{icon}</div>}
      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: theme.text }}>{title}</div>
      {description && <div style={{ fontSize: 12, color: theme.textTertiary, marginBottom: 18 }}>{description}</div>}
      {action}
    </div>
  );
}

// ─── Data Table ───
interface DataTableColumn<T> {
  header: string;
  width?: string;
  render: (item: T, index: number) => ReactNode;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  onRowClick?: (item: T) => void;
  keyField?: keyof T;
}

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  onRowClick,
  keyField = 'id' as keyof T,
}: DataTableProps<T>) {
  const gridTemplate = columns.map((c) => c.width || '1fr').join(' ');

  return (
    <div
      style={{
        background: theme.card,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          padding: '7px 16px',
          borderBottom: `1px solid ${theme.border}`,
          background: `${theme.bg2}cc`,
        }}
      >
        {columns.map((col) => (
          <div
            key={col.header}
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: theme.textTertiary,
              textTransform: 'uppercase',
              letterSpacing: 1,
            }}
          >
            {col.header}
          </div>
        ))}
      </div>
      {/* Rows */}
      {data.map((item, i) => (
        <div
          key={String(item[keyField]) || i}
          onClick={() => onRowClick?.(item)}
          style={{
            display: 'grid',
            gridTemplateColumns: gridTemplate,
            padding: '9px 16px',
            borderBottom: i < data.length - 1 ? `1px solid ${theme.border}` : 'none',
            alignItems: 'center',
            cursor: onRowClick ? 'pointer' : 'default',
            transition: 'background .1s',
          }}
          onMouseEnter={(e) => {
            if (onRowClick) e.currentTarget.style.background = theme.cardHover;
          }}
          onMouseLeave={(e) => {
            if (onRowClick) e.currentTarget.style.background = 'transparent';
          }}
        >
          {columns.map((col, ci) => (
            <div key={ci}>{col.render(item, i)}</div>
          ))}
        </div>
      ))}
      {data.length === 0 && (
        <div style={{ padding: 32, textAlign: 'center', color: theme.textTertiary, fontSize: 12 }}>No data</div>
      )}
    </div>
  );
}

// ─── Breadcrumb ─────────────────────────────────────────────────────────────
export { Breadcrumb, type BreadcrumbItem } from './Breadcrumb';
